import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CategoriaConsentimento, ConsentService } from 'app/core/services/consent.service';

interface OpcaoCookie {
  chave: string;
  titulo: string;
  descricao: string;
  ativo: boolean;
  categoria?: CategoriaConsentimento;
  bloqueado?: boolean;
}

@Component({
  selector: 'app-aceite-cookies',
  imports: [FormsModule, RouterLink],
  templateUrl: './aceite-cookies.component.html',
  styleUrls: ['./aceite-cookies.component.css']
})
export class AceiteCookiesComponent implements OnInit {

  private readonly router = inject(Router);
  private readonly consent = inject(ConsentService);
  private readonly destroyRef = inject(DestroyRef);

  readonly visivel = signal(false);
  readonly mostrarPreferencias = signal(false);

  opcoes: OpcaoCookie[] = [
    {
      chave: 'necessario',
      titulo: 'Necessário',
      descricao:
        'Permitem o funcionamento básico do site — navegação, segurança, autenticação, acessibilidade e ' +
        'carregamento correto das páginas — usando exclusivamente armazenamento local no seu ' +
        'navegador. São indispensáveis para a operação da Carta de Serviços e não podem ser desativados.',
      ativo: true,
      bloqueado: true
    },
    {
      chave: 'estatisticos',
      titulo: 'Estatísticos',
      descricao:
        'Permitem identificar seu endereço IP (via serviço externo api.ipify.org) para fins de ' +
        'estatística de acesso e para evitar avaliações duplicadas do atendimento. Coletados apenas com ' +
        'o seu consentimento. Sem ele, nenhuma consulta de IP é enviada.',
      ativo: true,
      categoria: 'estatisticos',
      bloqueado: true
    },
    {
      chave: 'preferencias',
      titulo: 'Preferências',
      descricao:
        'Permitem memorizar escolhas de visualização (tema, acessibilidade) e carregar recursos de ' +
        'aparência externos (fonte Poppins via Google Fonts). Sem consentimento, o site usa a fonte ' +
        'padrão do sistema.',
      ativo: true,
      categoria: 'preferencias',
      bloqueado: true
    },
    {
      chave: 'usoDados',
      titulo: 'Uso de dados',
      descricao:
        'Poderiam ser utilizados para medir a efetividade de comunicações digitais. Em portais públicos, ' +
        'apenas mediante finalidade legítima, transparência e consentimento do usuário. Atualmente nenhum ' +
        'recurso deste tipo está ativo neste portal.',
      ativo: true,
      categoria: 'usoDados',
      bloqueado: true
    }
  ];

  ngOnInit(): void {
    this.sincronizarToggles();

    this.consent.abrirPreferencias$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reabrirPreferencias());

    this.mostrarSeNaoDecidido();

    this.router.events
      .pipe(
        filter((evento) => evento instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.mostrarSeNaoDecidido());
  }

  private mostrarSeNaoDecidido(): void {
    if (this.consent.foiDecidido() || this.mostrarPreferencias()) {
      return;
    }
    this.visivel.set(true);
  }

  abrirPreferencias(): void {
    this.mostrarPreferencias.set(true);
  }

  voltar(): void {
    this.mostrarPreferencias.set(false);
  }

  aceitarTudo(): void {
    this.consent.aceitarTudo();
    this.sincronizarToggles();
    this.fechar();
  }

  recusarTudo(): void {
    this.consent.recusarTudo();
    this.sincronizarToggles();
    this.fechar();
  }

  salvarPreferencias(): void {
    this.consent.salvar({
      estatisticos: this.valorToggle('estatisticos'),
      preferencias: this.valorToggle('preferencias'),
      usoDados: this.valorToggle('usoDados'),
    });
    this.fechar();
  }

  fechar(): void {
    this.visivel.set(false);
    this.mostrarPreferencias.set(false);
  }

  private reabrirPreferencias(): void {
    this.sincronizarToggles();
    this.mostrarPreferencias.set(true);
    this.visivel.set(true);
  }

  private sincronizarToggles(): void {
    const estado = this.consent.estado();
    this.opcoes.forEach((opcao) => {
      if (opcao.bloqueado) {
        opcao.ativo = true;
      } else if (opcao.categoria) {
        opcao.ativo = estado[opcao.categoria] === true;
      }
    });
  }

  private valorToggle(categoria: CategoriaConsentimento): boolean {
    const opcao = this.opcoes.find((o) => o.categoria === categoria);
    return opcao?.ativo === true;
  }
}
